import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { getCustomers } from "../../../services/customer.services";
import { setAllCustomers } from "../../../state/reducers/customerSlice";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function CustomerDashboard() {
  const dispatch = useDispatch();
  const customers = useSelector((state) => state.customer.customers);
  const [customerData, setCustomerData] = useState({});
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [chartPeriod, setChartPeriod] = useState("year");
  const [isLoading, setIsLoading] = useState(true);
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  useEffect(() => {
    const fetchAllCustomers = async () => {
      try {
        setIsLoading(true);
        const response = await getCustomers();
        const allData = response.data.data || [];
        dispatch(setAllCustomers(allData));
        setCustomerData(response.data);
        prepareChartData(response.data);
      } catch (err) {
        console.error("Error fetching all customers:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllCustomers();
  }, [dispatch]);

  const prepareChartData = (data) => {
    if (!data || !data.monthly_counts) return;

    const monthlyCounts = data.monthly_counts;
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    let months, counts, labels;

    if (chartPeriod === "3months") {
      // Last 3 months
      labels = [];
      counts = [];
      for (let i = 2; i >= 0; i--) {
        const date = new Date(currentYear, currentMonth - i, 1);
        const monthKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;
        const monthName = date.toLocaleDateString("en-US", { month: "short" });
        labels.push(monthName);
        counts.push(monthlyCounts[monthKey] || 0);
      }
    } else if (chartPeriod === "6months") {
      // Last 6 months
      labels = [];
      counts = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date(currentYear, currentMonth - i, 1);
        const monthKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;
        const monthName = date.toLocaleDateString("en-US", { month: "short" });
        labels.push(monthName);
        counts.push(monthlyCounts[monthKey] || 0);
      }
    } else {
      // Full year
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      labels = monthNames;
      counts = Array(12).fill(0);

      Object.keys(monthlyCounts).forEach((key) => {
        const [year, month] = key.split("-");
        if (parseInt(year) === currentYear) {
          const monthIndex = parseInt(month, 10) - 1;
          counts[monthIndex] = monthlyCounts[key];
        }
      });
    }

    // Create gradient
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, "rgba(34, 197, 94, 0.3)");
    gradient.addColorStop(1, "rgba(34, 197, 94, 0.05)");

    setChartData({
      labels,
      datasets: [
        {
          label: "New Customers",
          data: counts,
          borderColor: "rgb(34, 197, 94)",
          backgroundColor: gradient,
          fill: true,
          tension: 0.4,
          borderWidth: 3,
          pointBackgroundColor: "rgb(34, 197, 94)",
          pointBorderColor: "#fff",
          pointBorderWidth: 3,
          pointRadius: 5,
          pointHoverRadius: 8,
          pointHoverBackgroundColor: "rgb(34, 197, 94)",
          pointHoverBorderColor: "#fff",
          pointHoverBorderWidth: 3,
          shadowColor: "rgba(34, 197, 94, 0.3)",
          shadowBlur: 10,
        },
      ],
    });
  };

  // Update chart when period changes
  useEffect(() => {
    if (customerData.monthly_counts) {
      prepareChartData(customerData);
    }
  }, [chartPeriod, customerData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          font: {
            size: 12,
            weight: "500",
          },
          color: "#374151",
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgb(34, 197, 94)",
        borderWidth: 2,
        cornerRadius: 8,
        padding: 12,
        displayColors: true,
        callbacks: {
          title: (context) => `${context[0].label} ${new Date().getFullYear()}`,
          label: (context) => `New Customers: ${context.parsed.y}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "#F3F4F6",
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#6B7280",
          font: {
            size: 11,
            weight: "400",
          },
          padding: 8,
          callback: function (value) {
            return Number.isInteger(value) ? value : "";
          },
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#6B7280",
          font: {
            size: 11,
            weight: "500",
          },
          padding: 8,
        },
      },
    },
    elements: {
      point: {
        hoverBorderWidth: 3,
      },
    },
  };

  const periodOptions = [
    { value: "3months", label: "3M" },
    { value: "6months", label: "6M" },
    { value: "year", label: "1Y" },
  ];

  const getChartStats = () => {
    if (!chartData.datasets[0]?.data) return { total: 0, growth: 0 };

    const data = chartData.datasets[0].data;
    const total = data.reduce((acc, val) => acc + val, 0);
    const lastMonth = data[data.length - 1] || 0;
    const secondLastMonth = data[data.length - 2] || 0;
    const growth =
      secondLastMonth > 0
        ? ((lastMonth - secondLastMonth) / secondLastMonth) * 100
        : 0;

    return { total, growth };
  };

  const { total, growth } = getChartStats();

  return (
    <div className="w-full">
      {/* Main container with responsive padding */}
      <div className="p-3 sm:p-4 md:p-6 bg-white rounded-lg">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">
            Customer Dashboard
          </h1>
        </div>

        {/* Main content area */}
        <div className="flex flex-col xl:flex-row gap-4 sm:gap-6">
          {/* Customer Stats Section */}
          <div className="w-full xl:w-1/2">
            <div className="bg-white border-2 border-purple-500 rounded-lg shadow-md p-4 sm:p-6 h-full">
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-700 mb-4 sm:mb-6">
                Customer Statistics
              </h2>

              {/* Stats grid - responsive layout */}
              <div className="flex flex-col justify-center h-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Total Customers Card */}
                  <div className="bg-gradient-to-br from-green-100 to-green-200 p-4 sm:p-6 rounded-lg shadow-sm border border-green-300 transition-transform hover:scale-105">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                          Total Customers
                        </p>
                        <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-green-800 break-words">
                          {customerData.total_customer || 0}
                        </h3>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm sm:text-lg font-bold">
                            👥
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* This Month Card */}
                  <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-4 sm:p-6 rounded-lg shadow-sm border border-blue-300 transition-transform hover:scale-105">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 font-medium mb-1">
                          This Month
                        </p>
                        <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-blue-800 break-words">
                          {chartData.datasets[0]?.data
                            ? chartData.datasets[0].data[
                                new Date().getMonth()
                              ] || 0
                            : 0}
                        </h3>
                      </div>
                      <div className="flex-shrink-0 ml-2">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm sm:text-lg font-bold">
                            📈
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Chart Section */}
          <div className="w-full xl:w-1/2">
            <div className="bg-white border-2 border-purple-500 rounded-lg shadow-md p-4 sm:p-6 h-full">
              {/* Chart Header with Controls */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-700 mb-2">
                    Customer Growth
                  </h2>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className="text-xs sm:text-sm text-gray-600">
                      <span className="font-medium">Period Total: </span>
                      <span className="font-bold text-green-600">{total}</span>
                    </div>
                    {growth !== 0 && (
                      <div className="text-xs sm:text-sm">
                        <span
                          className={`font-bold ${
                            growth >= 0 ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {growth >= 0 ? "↗" : "↘"}{" "}
                          {Math.abs(growth).toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Period Selector */}
                <div className="flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
                  {periodOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setChartPeriod(option.value)}
                      className={`flex-1 sm:flex-none px-3 py-1 text-xs sm:text-sm font-medium rounded-md transition-all duration-200 ${
                        chartPeriod === option.value
                          ? "bg-white text-purple-600 shadow-sm"
                          : "text-gray-600 hover:text-gray-800"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chart container with proper height management */}
              <div className="relative w-full h-48 sm:h-56 md:h-64 lg:h-72">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-purple-500 mx-auto mb-2"></div>
                      <p className="text-gray-500 text-xs sm:text-sm">
                        Loading chart...
                      </p>
                    </div>
                  </div>
                ) : chartData.labels.length > 0 ? (
                  <div className="relative h-full w-full">
                    <Line data={chartData} options={options} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <span className="text-gray-400 text-lg sm:text-2xl">
                          📊
                        </span>
                      </div>
                      <p className="text-gray-500 text-xs sm:text-sm">
                        No data available
                      </p>
                      <p className="text-gray-400 text-xs mt-1">
                        Check back later for updates
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerDashboard;
